<template>
  <VTDropdownPill :is-enabled="isEditable" @confirm="handleSave" @clear="handleReset" @click="handlePropagation">
    <template #dropDownHeader>
      <div :class="$style.campaignSection">
        <div :class="$style.label">Campaign</div>
        <div :class="$style.campaign">{{ innerSelectedCampaign?.name ?? "No campaign" }}</div>
      </div>
    </template>
    <template #dropdown-content>
      <div :class="$style.campaignManager">
        <VTMultiselect
          v-if="campaignOptions"
          :items="campaignOptions"
          :selected-items="selectedCampaigns"
          :enable-search="true"
          :selection-key="'value'"
          :has-add-section="false"
          :allow-deletion="false"
          @toggle-selection="handleSelectionUpdate"
        ></VTMultiselect>
      </div>
    </template>
  </VTDropdownPill>
</template>

<script setup lang="ts">
import VTDropdownPill from "@/components/VTDropdownPill";
import VTMultiselect from "@/components/VTMultiSelect/VTMultiSelect.vue";
import useCampaigns from "@/modules/Offers/hooks/useCampaigns";
import { CampaignOption, CampaignSelection } from "@/modules/Offers/types/types";
import { computed, ref, watch } from "vue";

const props = defineProps({
  campaignId: {
    type: String,
    default: null,
  },
  gameId: {
    type: String,
    required: true,
  },
  versionId: {
    type: String,
    required: true,
  },
  isEditable: {
    type: Boolean,
    default: true,
  },
});

const emits = defineEmits(["update:selected-campaign"]);

const selectedCampaigns = ref<CampaignSelection[]>([]);
const innerSelectedCampaign = ref<CampaignSelection | null>(null);

const { campaigns } = useCampaigns(props.gameId);

const campaignOptions = computed<CampaignOption[]>(() => {
  const isThereAnySelectedCampaign = selectedCampaigns.value.length > 0;
  return (
    campaigns.value?.map((campaign) => ({
      name: campaign.name,
      value: campaign.id,
      isDeletable: isThereAnySelectedCampaign ? selectedCampaigns.value[0].value !== campaign.id : true,
    })) ?? []
  );
});

/**
 * Event handlers
 */
function handlePropagation(event: Event) {
  event.stopPropagation();
}

function handleSelectionUpdate(currentCampaignSelection: CampaignSelection) {
  const isThereAnySelectedCampaign = selectedCampaigns.value.length > 0;
  if (isThereAnySelectedCampaign && selectedCampaigns.value[0].value === currentCampaignSelection.value) {
    selectedCampaigns.value = [];
  } else {
    selectedCampaigns.value = [currentCampaignSelection];
  }
}
async function handleSave() {
  const isThereAnySelectedCampaign = selectedCampaigns.value.length > 0;
  emits("update:selected-campaign", { value: isThereAnySelectedCampaign ? selectedCampaigns.value[0].value : null });
}

function handleReset() {
  emits("update:selected-campaign", {
    value: props.campaignId,
  });
}

/**
 * Watchers
 */
watch(
  () => [props.campaignId, campaigns],
  () => {
    const currentSelection = campaigns.value?.find((campaign) => campaign.id === props.campaignId);
    if (currentSelection) {
      innerSelectedCampaign.value = {
        value: currentSelection.id,
        name: currentSelection.name,
        selected: true,
      };

      selectedCampaigns.value = [
        {
          value: currentSelection.id,
          name: currentSelection.name,
          selected: true,
        },
      ];
    } else {
      innerSelectedCampaign.value = null;
      selectedCampaigns.value = [];
    }
  },
  {
    immediate: true,
    deep: true,
  }
);
</script>

<style module>
.campaignSection {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.campaign {
  background-color: var(--vd-color-blue-light-9);
  border-radius: 8px;
  padding: 4px 8px;
}

.label {
  font-size: 14px;
  font-weight: 600;
  color: var(--vd-color-text-light-2);
}

.campaignManager {
  width: 300px;
}
</style>
